import React, { useMemo } from "react";
import { classNames } from "Utils/utils";

const ANNUAL_DISCOUNT_PERCENT = 25;
const MONTHS_PER_YEAR = 12;

const parseDollarAmount = (priceString) => {
  const match = priceString?.match(/\$([\d.]+)/);
  return match ? parseFloat(match[1]) : null;
};

const formatPrice = (amount) =>
  Number.isInteger(amount) ? amount.toString() : amount.toFixed(2);

const PricingItemCard = (props) => {
  const {
    renderFor,
    data,
    monthlyPriceData = {},
    handleChange,
    selectedData,
    isSubscriptionNotChangeable,
    disabledParticipantTiers = new Set(),
    downgradeDisabledTiers = new Set(),
    currentParticipantCount = 0,
    currentPlanPriceId = null,
    currentPlanParticipantLabel = null,
  } = props;

  const isTierDisabled = (participantLabel) => disabledParticipantTiers.has(participantLabel);
  const isDowngradeTier = (participantLabel) => downgradeDisabledTiers.has(participantLabel);

  /** Cell is the user's current plan — not selectable (by price_id). */
  const isCurrentPlanCell = (pricing) =>
    Boolean(currentPlanPriceId && pricing?.price_id === currentPlanPriceId);

  /** Row is the current plan tier (by participant label) — show "Current" in all three columns. */
  const isCurrentPlanTierRow = (participantLabel) =>
    currentPlanParticipantLabel != null &&
    String(participantLabel ?? "").trim() === currentPlanParticipantLabel.trim();

  const dataToMap = useMemo(() => {
    if (renderFor === "participants") {
      return data;
    }
    return data ? Object.entries(data) : [];
  }, [data]);

  return (
    <div className='border border-1 border-gray-400 rounded-lg h-full flex flex-col w-full'>
      <div className='pt-8 pb-6 px-4 shrink-0'>
        <span className='bg-gradient text-white font-extrabold py-2 flex rounded-full justify-center items-center'>
          {renderFor === "year_1" ? (
            "Monthly Price"
          ) : renderFor === "yearly" ? (
            <div className='flex items-center gap-x-2'>
              <span>Annually Price</span>
              <span className='text-xs bg-red-500 text-white px-2 py-1 rounded-full'>
                {ANNUAL_DISCOUNT_PERCENT}% Off
              </span>
            </div>
          ) : (
            "Participants"
          )}
        </span>
      </div>
      <div className='flex-1 min-h-0 flex flex-col'>
      {renderFor === "year_1" &&
        dataToMap.map(([participants, pricing], index) => {
          const isCurrent = isCurrentPlanCell(pricing);
          const disabled =
            isTierDisabled(participants) ||
            isCurrent ||
            (pricing && Object.values(pricing).length === 0);
          return (
            <div
              key={index}
              role='button'
              tabIndex={disabled ? -1 : 0}
              onClick={() => {
                if (isSubscriptionNotChangeable || disabled) return;
                if (pricing && Object.values(pricing).length === 0) return;
                handleChange("price", { [`${participants}`]: { ...pricing } });
              }}
              title={
                isCurrent
                  ? "Your current plan — choose a different option to change"
                  : isTierDisabled(participants)
                  ? isDowngradeTier(participants)
                    ? "Downgrades are not allowed. Choose your current tier or higher."
                    : `Your current usage (${currentParticipantCount} participants) exceeds this tier. Choose a higher tier.`
                  : undefined
              }
              className={classNames(
                "transition duration-300 ease-in py-4 px-4 min-h-[3.25rem] flex flex-col justify-center items-center leading-normal",
                disabled ? "bg-gray-200 opacity-60 cursor-not-allowed" : "hover:bg-gray-100",
                !disabled &&
                  pricing &&
                  Object.values(pricing).length > 0 &&
                  selectedData?.selectedPrice?.price_id === pricing.price_id
                  ? "bg-gradient text-white"
                  : "text-black"
              )}
            >
              <span className='flex flex-wrap items-center justify-center gap-x-2 gap-y-0.5 text-center'>
                {isCurrent && (
                  <span className='inline-flex shrink-0 rounded px-1.5 py-0.5 text-xs font-semibold bg-emerald-500 text-white'>
                    Current
                  </span>
                )}
                <span>{pricing?.monthly ?? "-"}</span>
              </span>
            </div>
          );
        })}
      {renderFor === "yearly" &&
        dataToMap.map(([participants, pricing], index) => {
          const discountedAmount = parseDollarAmount(pricing?.monthly);
          const monthlyAmount = parseDollarAmount(
            monthlyPriceData?.[participants]?.monthly
          );

          const discountedBasePrice =
            discountedAmount != null ? formatPrice(discountedAmount) : null;

          const originalBasePrice =
            monthlyAmount != null
              ? formatPrice(monthlyAmount * MONTHS_PER_YEAR)
              : discountedAmount != null
              ? formatPrice(
                  discountedAmount / (1 - ANNUAL_DISCOUNT_PERCENT / 100)
                )
              : null;
          const isCurrent = isCurrentPlanCell(pricing);
          const disabled =
            isTierDisabled(participants) ||
            isCurrent ||
            (pricing && Object.values(pricing).length === 0);
          return (
            <div
              key={index}
              role='button'
              tabIndex={disabled ? -1 : 0}
              onClick={() => {
                if (isSubscriptionNotChangeable || disabled) return;
                if (pricing && Object.values(pricing).length === 0) return;
                handleChange("price", { [`${participants}`]: { ...pricing } });
              }}
              title={
                isCurrent
                  ? "Your current plan — choose a different option to change"
                  : isTierDisabled(participants)
                  ? isDowngradeTier(participants)
                    ? "Downgrades are not allowed. Choose your current tier or higher."
                    : `Your current usage (${currentParticipantCount} participants) exceeds this tier. Choose a higher tier.`
                  : undefined
              }
              className={classNames(
                "transition duration-300 ease-in py-4 px-4 min-h-[3.25rem] flex flex-col justify-center items-center leading-normal",
                disabled ? "bg-gray-200 opacity-60 cursor-not-allowed" : "hover:bg-gray-100",
                !disabled &&
                  pricing &&
                  Object.values(pricing).length > 0 &&
                  selectedData?.selectedPrice?.price_id === pricing.price_id
                  ? "bg-gradient text-white"
                  : "text-black"
              )}
            >
              <span className='flex flex-wrap items-center justify-center gap-x-2 gap-y-0.5 text-center leading-normal'>
                {isCurrent && (
                  <span className='inline-flex shrink-0 rounded px-1.5 py-0.5 text-xs font-semibold bg-emerald-500 text-white'>
                    Current
                  </span>
                )}
                <span className='inline-flex items-baseline justify-center gap-x-1.5 shrink-0'>
                  <span className='line-through tabular-nums'>${originalBasePrice || "-"}</span>
                  <span className='tabular-nums'>${discountedBasePrice || "-"}</span>
                  <span className='whitespace-nowrap'>Plus GST/year</span>
                </span>
              </span>
            </div>
            // <div
            //   key={index}
            //   role='button'
            //   tabIndex={0}
            //   onClick={() => {
            //     if (isSubscriptionNotChangeable) return;
            //     if (pricing && Object.values(pricing).length === 0) return;
            //     handleChange("price", { [`${participants}`]: { ...pricing } });
            //   }}
            //   className={classNames(
            //     "transition duration-300 ease-in py-4 flex flex-col items-center",
            //     pricing && Object.values(pricing).length === 0
            //       ? "bg-gray-200 opacity-50 cursor-auto"
            //       : "hover:bg-gray-100",
            //     pricing &&
            //       Object.values(pricing).length > 0 &&
            //       selectedData?.selectedPrice?.price_id === pricing.price_id
            //       ? "bg-gradient text-white"
            //       : "text-black"
            //   )}
            // >
            //   <div className='flex items-center gap-2'>
            //      className={`text-sm line-through ${
            //         pricing &&
            //         Object.values(pricing).length > 0 &&
            //         selectedData?.selectedPrice?.price_id === pricing.price_id
            //           ? "text-white-500"
            //           : "text-gray-500"
            //       }`}
            //     >
            //       {originalBasePrice ? `${originalBasePrice}` : "-"}
            //     </span>  <span

            //     <span className='text-md font-bold'>
            //     {pricing?.monthly ?? "-"}
            //     </span>

            //   </div>
            // </div>
          );
        })}

      {renderFor === "participants" &&
        dataToMap.map((item, index) => {
          const disabled = item?.includes("200+") || isTierDisabled(item);
          const isCurrentPlanTier =
            currentPlanParticipantLabel != null &&
            String(item ?? "").trim() === currentPlanParticipantLabel.trim();
          return (
            <div
              key={index}
              role='button'
              tabIndex={disabled ? -1 : 0}
              onClick={() => {
                if (isSubscriptionNotChangeable || disabled) return;
                if (item?.includes("200+")) return;
                handleChange("noOfParticipants", item);
              }}
              title={
                isTierDisabled(item)
                  ? isDowngradeTier(item)
                    ? "Downgrades are not allowed. Choose your current tier or higher."
                    : `Your current usage (${currentParticipantCount} participants) exceeds this tier. Choose a higher tier.`
                  : undefined
              }
              className={classNames(
                "transition duration-300 ease-in py-4 px-4 min-h-[3.25rem] flex flex-col justify-center items-center leading-normal",
                disabled ? "bg-gray-200 opacity-50 cursor-not-allowed" : "hover:bg-gray-100",
                !disabled && selectedData?.selectedParticipants === item
                  ? " bg-gradient text-white"
                  : "text-black"
              )}
            >
              <span className='flex flex-wrap items-center justify-center gap-x-2 gap-y-0.5 text-center'>
                {isCurrentPlanTier && (
                  <span className='inline-flex shrink-0 rounded px-1.5 py-0.5 text-xs font-semibold bg-emerald-500 text-white'>
                    Current
                  </span>
                )}
                <span>{item}</span>
              </span>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default PricingItemCard;
